import * as React from 'react';
import { useForm, type FormValidateOrFn } from '@tanstack/react-form';
import type { ZodType } from 'zod';
import { useValidationNavigation } from './useValidationNavigation';

export type FormApi = ReturnType<typeof useForm>;

interface FormContextValue {
  form: FormApi;
  isPending?: boolean;
}

const FormContext = React.createContext<FormContextValue | null>(null);

export function useFormContext() {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error('Form components must be used within a Form provider');
  }
  return context;
}

interface FormProps<TFormData> {
  children: React.ReactNode;
  schema: ZodType<TFormData>;
  defaultValues: Partial<TFormData>;
  onSubmit: (values: TFormData) => Promise<void> | void;
  isPending?: boolean;
  className?: string;
  id?: string;
  formRef?: React.MutableRefObject<FormApi | null>;
}

export function Form<TFormData>({
  children,
  schema,
  defaultValues,
  onSubmit,
  isPending,
  className = 'flex flex-col gap-6',
  id,
  formRef,
}: FormProps<TFormData>) {
  const { elementRef, requestNavigation } = useValidationNavigation(isPending);
  const formValidator = schema as unknown as FormValidateOrFn<TFormData>;
  const form = useForm({
    defaultValues: defaultValues as TFormData,
    validators: {
      onMount: formValidator,
      onChange: formValidator,
      onSubmit: formValidator,
    },
    onSubmitInvalid: requestNavigation,
    onSubmit: async ({ value }) => {
      try {
        await onSubmit(value as TFormData);
      } finally {
        requestNavigation();
      }
    },
  });

  if (formRef) formRef.current = form as FormApi;

  return (
    <FormContext.Provider value={{ form: form as FormApi, isPending }}>
      <form
        ref={elementRef}
        id={id}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className={className}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}
